import { apiClient } from "@/lib/api";
import type { Paginated, PaginationParams } from "@/types";

export type SettlementStatusBucket =
  | "unpaid_uninvoiced"
  | "unpaid_invoiced"
  | "paid_uninvoiced";

export type SettlementFlowKind = "UPFRONT" | "RECEIPT_BASED" | "SUPPLIER_PAYOUT";

export type SettlementInvoiceStatus = "PENDING" | "INVOICED";
export type SettlementPaymentStatus = "UNPAID" | "PAID" | "NOT_APPLICABLE";
export type SettlementCollectionStatus =
  | "UNCOLLECTED"
  | "COLLECTED"
  | "NOT_APPLICABLE";

export interface SettlementPayeeRow {
  user_id: string;
  role: string;
  name: string;
  email: string;
  currency: string;
  open_line_count: number;
  unpaid_count: number;
  uninvoiced_count: number;
  uncollected_count: number;
  open_payable_cents: number;
  open_invoiceable_cents: number;
  open_collectible_cents: number;
  pending_supplier_invoice_count: number;
  open_pending_supplier_invoice_cents: number;
  has_legal_info: boolean;
  region_code: string | null;
}

export interface SettlementLineRow {
  id: string;
  seller_user_id: string;
  economic_event_id: string;
  event_allocation_line_id: string;
  booking_id: string | null;
  checkout_id: string | null;
  activity_id: string | null;
  activity_name: string | null;
  activity_payment_model: string | null;
  line_kind: string;
  seller_role: string;
  flow_kind: SettlementFlowKind;
  currency: string;
  source_amount_cents: number;
  payable_amount_cents: number;
  invoiceable_amount_cents: number;
  commission_rate: number | null;
  invoice_status: SettlementInvoiceStatus;
  payment_status: SettlementPaymentStatus;
  collection_status: SettlementCollectionStatus;
  invoice_document_id: string | null;
  invoice_document_number?: string | null;
  invoice_document_kind?: string | null;
  supplier_invoice_number?: string | null;
  payment_batch_id: string | null;
  payment_reference?: string | null;
  collection_batch_id?: string | null;
  collection_reference?: string | null;
  invoiced_at: string | null;
  paid_at: string | null;
  collected_at?: string | null;
  last_summary_email_at?: string | null;
  summary_email_count: number;
  event_type: string;
  event_created_at: string;
  auto_selected: boolean;
}

export interface SettlementSummaryEmailResponse {
  sent: boolean;
  recipient: string;
  line_ids: string[];
  summary_email_count: number;
  last_summary_email_at: string | null;
  detail?: string | null;
}

/** Open work summed over every payee with open lines, for one currency. */
export interface SettlementPayeeTotals {
  currency: string;
  open_payable_cents: number;
  payable_payee_count: number;
  open_invoiceable_cents: number;
  invoiceable_payee_count: number;
  open_collectible_cents: number;
  collectible_payee_count: number;
  open_pending_supplier_invoice_cents: number;
  pending_supplier_invoice_payee_count: number;
}

export type PaginatedSettlementPayees = Paginated<SettlementPayeeRow> & {
  /** One entry per currency; independent of the list filters. */
  totals: SettlementPayeeTotals[];
};

export interface SettlementPayeeLinesResponse {
  payee: SettlementPayeeRow;
  lines: SettlementLineRow[];
}

export interface SettlementDocumentRow {
  id: string;
  document_kind: string;
  document_number: string;
  file_url: string | null;
  external_doc_ref?: string | null;
  total_amount_cents: number;
  currency: string;
  created_at: string;
}

export type ReplaceSupplierInvoiceInput = {
  documentId: string;
  supplierInvoiceNumber: string;
  supplierInvoiceDate?: string;
  file: File;
  note?: string;
};

/** Response shape shared by mark-invoiced / mark-paid / record-collection / supplier-invoice. */
export interface SettlementActionResponse {
  batch_id: string;
  updated_line_ids: string[];
  total_payable_cents: number;
  total_invoiceable_cents: number;
  total_collected_cents: number;
  economic_event_id: string | null;
  documents: SettlementDocumentRow[];
}

export type RevolutPayoutMode = "direct" | "draft";

export type SettlementPayoutApiStatus =
  | "CREATED"
  | "PENDING"
  | "AWAITING_APP"
  | "PENDING_RECONCILIATION"
  | "COMPLETED"
  | "FAILED"
  | "REVERTED";

/** Discriminator when status is `PENDING_RECONCILIATION` (prefer over parsing `failure_reason`). */
export type SettlementPayoutReconcilePhase =
  | "uncertain_payment"
  | "ledger_finalize_blocked";

export interface SettlementRevolutPayoutResponse {
  id: string;
  seller_user_id: string;
  line_ids: string[];
  amount_cents: number;
  currency: string;
  mode: "DIRECT" | "DRAFT";
  status: SettlementPayoutApiStatus;
  reference: string;
  note: string | null;
  idempotency_key: string;
  revolut_transaction_id: string | null;
  revolut_draft_id: string | null;
  revolut_state: string | null;
  reconcile_phase: SettlementPayoutReconcilePhase | null;
  failure_reason: string | null;
  instructions: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface ActiveRevolutSettlementPayoutResponse {
  payout: SettlementRevolutPayoutResponse | null;
}

export type SettlementPayeeStatusFilter = "to_be_paid" | "to_be_invoiced";

export const adminSettlementKeys = {
  all: ["admin", "settlements"] as const,
  /** Prefix shared with SettlementsPayeesPage list queries. */
  payeeLists: () => ["admin", "settlement-payees"] as const,
  payeeList: (params: {
    role: string;
    status: string;
    search: string;
    regionId: string | null;
    page: number;
  }) => [...adminSettlementKeys.payeeLists(), params] as const,
  payeeLines: (userId: string) =>
    [...adminSettlementKeys.all, "payee-lines", userId] as const,
  activeRevolutPayout: (userId: string) =>
    [...adminSettlementKeys.all, "revolut-active", userId] as const,
};

export const adminSettlementService = {
  async listPayees(
    params?: PaginationParams & {
      role?: string;
      search?: string;
      has_open_lines?: boolean;
      has_legal_info?: boolean;
      status_filter?: SettlementPayeeStatusFilter;
      region_id?: string;
    },
  ): Promise<PaginatedSettlementPayees> {
    const { data } = await apiClient.get<PaginatedSettlementPayees>(
      "/admin/settlements/payees",
      { params },
    );
    return data;
  },

  async getPayeeLines(
    userId: string,
    statusBucket?: SettlementStatusBucket,
  ): Promise<SettlementPayeeLinesResponse> {
    const { data } = await apiClient.get<SettlementPayeeLinesResponse>(
      `/admin/settlements/payees/${userId}/lines`,
      { params: statusBucket ? { status_bucket: statusBucket } : undefined },
    );
    return data;
  },

  async markInvoiced(
    userId: string,
    payload: { line_ids: string[]; note?: string },
  ): Promise<SettlementActionResponse> {
    const { data } = await apiClient.post<SettlementActionResponse>(
      `/admin/settlements/payees/${userId}/mark-invoiced`,
      payload,
    );
    return data;
  },

  async markPaid(
    userId: string,
    payload: { line_ids: string[]; reference: string; note?: string },
  ): Promise<SettlementActionResponse> {
    const { data } = await apiClient.post<SettlementActionResponse>(
      `/admin/settlements/payees/${userId}/mark-paid`,
      payload,
    );
    return data;
  },

  async initiateRevolutPayout(
    userId: string,
    payload: { line_ids: string[]; mode: RevolutPayoutMode; reference: string; note?: string },
  ): Promise<SettlementRevolutPayoutResponse> {
    const { data } = await apiClient.post<SettlementRevolutPayoutResponse>(
      `/admin/settlements/payees/${userId}/revolut-payout`,
      payload,
    );
    return data;
  },

  async getActiveRevolutPayout(userId: string): Promise<ActiveRevolutSettlementPayoutResponse> {
    const { data } = await apiClient.get<ActiveRevolutSettlementPayoutResponse>(
      `/admin/settlements/payees/${userId}/revolut-payouts/active`,
    );
    return data;
  },

  async getRevolutPayout(payoutId: string): Promise<SettlementRevolutPayoutResponse> {
    const { data } = await apiClient.get<SettlementRevolutPayoutResponse>(
      `/admin/settlements/revolut-payouts/${payoutId}`,
    );
    return data;
  },

  async downloadSettlementDocumentPdf(documentId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/admin/settlements/documents/${documentId}/pdf`,
      { responseType: "blob" },
    );
    return data;
  },

  async sendSummaryEmail(
    userId: string,
    payload: { line_ids: string[] },
  ): Promise<SettlementSummaryEmailResponse> {
    const { data } = await apiClient.post<SettlementSummaryEmailResponse>(
      `/admin/settlements/payees/${userId}/send-summary`,
      payload,
    );
    return data;
  },

  async recordCollection(
    userId: string,
    payload: { line_ids: string[]; reference: string; note?: string },
  ): Promise<SettlementActionResponse> {
    const { data } = await apiClient.post<SettlementActionResponse>(
      `/admin/settlements/payees/${userId}/record-collection`,
      payload,
    );
    return data;
  },

  async recordSupplierInvoice(
    userId: string,
    payload: {
      line_ids: string[];
      file: File;
      supplier_invoice_number: string;
      supplier_invoice_date?: string;
      total_cents: number;
      note?: string;
    },
  ): Promise<SettlementActionResponse> {
    const form = new FormData();
    payload.line_ids.forEach((id) => form.append("line_ids", id));
    form.append("supplier_invoice_number", payload.supplier_invoice_number);
    form.append("file", payload.file);
    form.append("total_cents", String(payload.total_cents));
    if (payload.supplier_invoice_date)
      form.append("supplier_invoice_date", payload.supplier_invoice_date);
    if (payload.note) form.append("note", payload.note);
    const { data } = await apiClient.post<SettlementActionResponse>(
      `/admin/settlements/payees/${userId}/record-supplier-invoice`,
      form,
    );
    return data;
  },

  async replaceSupplierInvoice(
    payload: ReplaceSupplierInvoiceInput,
  ): Promise<SettlementDocumentRow> {
    const form = new FormData();
    form.append("supplier_invoice_number", payload.supplierInvoiceNumber);
    form.append("file", payload.file);
    if (payload.supplierInvoiceDate)
      form.append("supplier_invoice_date", payload.supplierInvoiceDate);
    if (payload.note) form.append("note", payload.note);
    const { data } = await apiClient.put<SettlementDocumentRow>(
      `/admin/settlements/documents/${payload.documentId}/supplier-invoice`,
      form,
    );
    return data;
  },

  async downloadSupplierInvoice(documentId: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/admin/settlements/documents/${documentId}/supplier-invoice`,
      { responseType: "blob" },
    );
    return data;
  },
};
